import {
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  MenuList,
  Popper,
  Tooltip,
} from "@mui/material";
import React, { forwardRef, useState } from "react";

import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";
import { LoadingButton } from "@mui/lab";
import { FileDownloadOutlined } from "@mui/icons-material";
import { nuclearn_api_url } from "src/App";
import { authorizedUser, stripBearerPrefix } from "src/helpers/Auth";
import { NucToast } from "src/reusable/components/Toast";
import { DatasetLoadingButton } from "src/reusable/components/DatasetButtons";

import {
  useDownloadDatasetFile,
  DOCUMENT_LIKE_DATASET_TYPES,
} from "../services/DatasetServices";
import DatasetArchiveProgress, {
  DatasetArchiveCompletion,
} from "./DatasetArchiveProgress";

interface DatasetDownloadAllButtonProps {
  datasetId: number;
  datasetType: any;
  datasetName?: string;
  disabled?: boolean;
  iconOnly?: boolean;
  /**
   * Visual variant for the non-iconOnly trigger.
   * "outlined" (default) matches the plain outlined buttons in the dataset
   * viewer header. "dataset" uses the gray DatasetLoadingButton style that
   * sibling actions (Power BI, Analyze, Chat, Sync) use on the /datasets
   * list page.
   */
  buttonVariant?: "outlined" | "dataset";
}

const isDocumentLikeDataset = (datasetType: any): boolean =>
  DOCUMENT_LIKE_DATASET_TYPES.includes(datasetType?.name);

// Anchor-triggered downloads cannot attach Authorization headers, so the
// fetch endpoint also accepts auth via a ?token= query param (same pattern
// used by the SSE stream endpoint).
const appendAuthTokenToUrl = (url: string, token: string): string => {
  const normalizedToken = stripBearerPrefix(token).trim();
  if (!normalizedToken) return url;
  const parsed = new URL(url, window.location.origin);
  parsed.searchParams.set("token", normalizedToken);
  return parsed.toString();
};

const triggerBrowserDownload = (url: string, filename: string): void => {
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  // URL carries ?token=<jwt>; suppress Referer so a backend 302 to a
  // presigned storage URL doesn't leak the token into storage-server logs.
  link.referrerPolicy = "no-referrer";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const startArchiveBuild = async (
  datasetId: number,
  includeData: boolean,
): Promise<{ correlationId: string }> => {
  const response = await axios.post(
    `${nuclearn_api_url}/dataset-archive/${datasetId}/download-all/start`,
    { include_data: includeData },
  );
  return { correlationId: response.data.correlation_id };
};

const archiveFilename = (datasetName: string | undefined, datasetId: number) =>
  `${datasetName ?? "dataset"}_${datasetId}_archive.zip`;

// Backend signals "too large for the sync path" with either 413 (current)
// or 501 (legacy, still in DatasetServices.downloadDatasetFile). Treat
// both as the fallback trigger so the flow works across backend versions.
const SYNC_TOO_LARGE_STATUSES = new Set([413, 501]);

// Returns true if the sync endpoint served the zip; false when the backend
// signals "too large" so the caller can fall back to the async build flow.
const trySyncDatasetDownload = async (
  datasetId: number,
  datasetName: string | undefined,
  includeData: boolean,
): Promise<boolean> => {
  try {
    const response = await axios({
      url: `${nuclearn_api_url}/dataset-archive/${datasetId}/download-all`,
      method: "GET",
      responseType: "blob",
      params: { include_data: includeData },
    });
    const blobUrl = window.URL.createObjectURL(
      new Blob([response.data], { type: "application/zip" }),
    );
    try {
      triggerBrowserDownload(blobUrl, archiveFilename(datasetName, datasetId));
    } finally {
      window.URL.revokeObjectURL(blobUrl);
    }
    return true;
  } catch (error: any) {
    if (SYNC_TOO_LARGE_STATUSES.has(error?.response?.status)) return false;
    throw error;
  }
};

interface DownloadTriggerProps {
  busy: boolean;
  disabled: boolean;
  iconOnly: boolean;
  buttonVariant: "outlined" | "dataset";
  onOpenMenu: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const DownloadTrigger = forwardRef<HTMLButtonElement, DownloadTriggerProps>(
  ({ busy, disabled, iconOnly, buttonVariant, onOpenMenu }, ref) => {
    if (iconOnly) {
      return (
        <Tooltip title="Download">
          <IconButton
            ref={ref}
            size="small"
            onClick={onOpenMenu}
            disabled={disabled || busy}
            aria-label="Download"
            data-attr="datasets:btn:download-all-menu"
          >
            {busy ? <CircularProgress size={20} /> : <FileDownloadOutlined />}
          </IconButton>
        </Tooltip>
      );
    }
    if (buttonVariant === "dataset") {
      return (
        <DatasetLoadingButton
          ref={ref}
          size="small"
          startIcon={<FileDownloadOutlined />}
          onClick={onOpenMenu}
          loading={busy}
          loadingPosition="start"
          disabled={disabled || busy}
          data-attr="datasets:btn:download-all-menu"
        >
          Download
        </DatasetLoadingButton>
      );
    }
    return (
      <LoadingButton
        ref={ref}
        variant="outlined"
        color="inherit"
        size="small"
        startIcon={<FileDownloadOutlined />}
        onClick={onOpenMenu}
        loading={busy}
        loadingPosition="start"
        disabled={disabled || busy}
        data-attr="datasets:btn:download-all-menu"
        sx={{ textTransform: "none", minHeight: 32 }}
      >
        Download
      </LoadingButton>
    );
  },
);
DownloadTrigger.displayName = "DownloadTrigger";

interface DownloadOptionsMenuProps {
  anchorEl: HTMLElement | null;
  includeData: boolean;
  onToggleIncludeData: (value: boolean) => void;
  onClose: () => void;
  onStart: () => void;
}

const DownloadOptionsMenu: React.FC<DownloadOptionsMenuProps> = ({
  anchorEl,
  includeData,
  onToggleIncludeData,
  onClose,
  onStart,
}) => (
  <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onClose}>
    <MenuList dense>
      <MenuItem>
        <Tooltip
          title="Adds a CSV index of every record's UID and metadata (filename, source, tags, etc.) — separate from the artifact files themselves."
          placement="right"
        >
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={includeData}
                onChange={(e) => onToggleIncludeData(e.target.checked)}
              />
            }
            label="Include metadata CSV"
          />
        </Tooltip>
      </MenuItem>
      <MenuItem
        onClick={onStart}
        data-attr="datasets:btn:start-download"
        sx={{
          display: "flex",
          justifyContent: "center",
          padding: "8px 16px",
          margin: "8px 16px",
          backgroundColor: "primary.main",
          color: "white",
          borderRadius: "4px",
          "&:hover": { backgroundColor: "primary.dark" },
        }}
      >
        <DownloadIcon sx={{ marginRight: "8px" }} />
        Start Download
      </MenuItem>
    </MenuList>
  </Menu>
);

const DatasetDownloadAllButton: React.FC<DatasetDownloadAllButtonProps> = ({
  datasetId,
  datasetType,
  datasetName,
  disabled = false,
  iconOnly = false,
  buttonVariant = "outlined",
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [triggerEl, setTriggerEl] = useState<HTMLButtonElement | null>(null);
  const [includeData, setIncludeData] = useState(true);
  const [isStarting, setIsStarting] = useState(false);
  const [correlationId, setCorrelationId] = useState<string | null>(null);
  const [csvDownloadInFlight, setCsvDownloadInFlight] = useState(false);

  const { mutateAsync: downloadDataset } = useDownloadDatasetFile();

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const startDocumentDatasetDownload = async () => {
    setIsStarting(true);
    try {
      const servedSync = await trySyncDatasetDownload(
        datasetId,
        datasetName,
        includeData,
      );
      if (servedSync) return;
      const { correlationId: newId } = await startArchiveBuild(
        datasetId,
        includeData,
      );
      setCorrelationId(newId);
    } catch (error) {
      console.error("Failed to start archive build", error);
      NucToast({
        message:
          "Could not start the dataset download. Please try again later.",
        type: "error",
      });
    } finally {
      setIsStarting(false);
    }
  };

  const startCsvDatasetDownload = async () => {
    setCsvDownloadInFlight(true);
    try {
      await downloadDataset({
        datasetId,
        datasetType,
        datasetName,
        includeData,
      });
    } catch (error) {
      console.error("Error downloading dataset:", error);
      NucToast({
        message: "Could not download the dataset. Please try again later.",
        type: "error",
      });
    } finally {
      setCsvDownloadInFlight(false);
    }
  };

  const handleStartDownload = async () => {
    handleMenuClose();
    if (isDocumentLikeDataset(datasetType)) {
      await startDocumentDatasetDownload();
    } else {
      await startCsvDatasetDownload();
    }
  };

  const handleArchiveReady = (completion: DatasetArchiveCompletion) => {
    const token = authorizedUser().Authorization ?? "";
    const authedUrl = appendAuthTokenToUrl(completion.downloadUrl, token);
    triggerBrowserDownload(authedUrl, completion.filename);
    setCorrelationId(null);
  };

  const handleDismissProgress = () => setCorrelationId(null);

  const busy = isStarting || csvDownloadInFlight || correlationId !== null;

  return (
    <>
      <DownloadTrigger
        ref={setTriggerEl}
        busy={busy}
        disabled={disabled}
        iconOnly={iconOnly}
        buttonVariant={buttonVariant}
        onOpenMenu={handleMenuOpen}
      />
      <DownloadOptionsMenu
        anchorEl={anchorEl}
        includeData={includeData}
        onToggleIncludeData={setIncludeData}
        onClose={handleMenuClose}
        onStart={handleStartDownload}
      />
      <Popper
        open={correlationId !== null}
        anchorEl={triggerEl}
        placement="bottom-end"
        modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
      >
        {correlationId && (
          <DatasetArchiveProgress
            datasetId={datasetId}
            correlationId={correlationId}
            onComplete={handleArchiveReady}
            onDismiss={handleDismissProgress}
          />
        )}
      </Popper>
    </>
  );
};

export default DatasetDownloadAllButton;
